import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { FullPageStatus } from '../../components/FullPageStatus';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import type { FacebookPageOption } from '../../types/api';
import { useBusiness } from '../onboarding/useBusiness';
import { authorizeFacebook, exchangeFacebook, selectFacebookPage } from './integrationsApi';
import { knowledgeBaseSectionPath } from './sections';

/** Back to the tab that started this, with the one-time notice param the section reads. */
const CONNECTED_PATH = `${knowledgeBaseSectionPath('integrations')}&facebook=connected`;

const INTEGRATIONS_PATH = knowledgeBaseSectionPath('integrations');

const MISSING_PARAMS_MESSAGE = 'Facebook did not send back what we need. Try again.';

/** Message for the domain errors the exchange, select and authorize calls can answer with. */
function metaMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'INVALID_OAUTH_STATE':
        return 'This sign-in link is stale. Try again.';
      case 'INTEGRATION_DISABLED':
        return 'Facebook publishing is not configured on this server.';
      case 'FACEBOOK_NO_PAGES':
        return 'No Facebook Page was shared. Connect again and tick your Page.';
      case 'FACEBOOK_SELECTION_EXPIRED':
        return 'That took too long. Connect again.';
      case 'META_UNAVAILABLE':
        return 'Facebook did not answer. Try connecting again in a minute.';
      case 'META_REJECTED':
        return 'Facebook refused the connection. Try again.';
      default:
        break;
    }
    if (caught.status === 403) {
      return 'Only the account owner can connect Facebook.';
    }
  }
  return getErrorMessage(caught, 'That connection did not finish. Try again.');
}

/** Pages to choose from, and the ticket that proves the choice is this caller's. */
interface PageChoice {
  pages: FacebookPageOption[];
  ticket: string;
}

/**
 * The OAuth redirect target, `/integrations/meta/callback`.
 *
 * Same shape as `LinkedInCallbackPage`: Meta sends `code` and `state` back on
 * the query string and this page posts them through the authenticated client,
 * exactly once per mount (the code is single-use). One extra step: when the
 * user shared several Pages the exchange answers with the list instead of a
 * connection, and the user picks one here.
 *
 * Every failure offers "Try again", which starts a fresh authorize rather than
 * replaying this URL: the code in it is spent either way.
 */
export function MetaCallbackPage() {
  const [searchParams] = useSearchParams();
  const { businessId, isLoading: isBusinessLoading, error: businessError } = useBusiness();

  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [choice, setChoice] = useState<PageChoice | null>(null);
  const [pageId, setPageId] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const attemptedRef = useRef(false);

  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const deniedCode = searchParams.get('error');
  const hasCallbackParams = code !== null && state !== null;

  useEffect(() => {
    if (deniedCode !== null || !hasCallbackParams || !businessId) return;
    if (attemptedRef.current) return;
    if (code === null || state === null) return;

    attemptedRef.current = true;

    void (async () => {
      try {
        const result = await exchangeFacebook(businessId, { code, state });
        if (result.connection !== null) {
          setIsDone(true);
        } else if (result.selection_ticket !== null && result.pages.length > 0) {
          setChoice({ pages: result.pages, ticket: result.selection_ticket });
        } else {
          setError(MISSING_PARAMS_MESSAGE);
        }
      } catch (caught) {
        setError(metaMessage(caught));
      }
    })();
  }, [businessId, code, deniedCode, hasCallbackParams, state]);

  function handleRetry() {
    if (!businessId || isBusy) return;
    setIsBusy(true);
    void (async () => {
      try {
        const authorization = await authorizeFacebook(businessId);
        // This tab is leaving; the button stays disabled until it does.
        window.location.assign(authorization.authorization_url);
      } catch (caught) {
        setIsBusy(false);
        setError(metaMessage(caught));
      }
    })();
  }

  function handleSelect() {
    if (!businessId || !choice || !pageId || isBusy) return;
    setIsBusy(true);
    void (async () => {
      try {
        await selectFacebookPage(businessId, {
          selection_ticket: choice.ticket,
          page_id: pageId,
        });
        setIsDone(true);
      } catch (caught) {
        setIsBusy(false);
        setChoice(null);
        setError(metaMessage(caught));
      }
    })();
  }

  if (isDone) {
    return <Navigate to={CONNECTED_PATH} replace />;
  }

  if (deniedCode !== null || error !== null || !hasCallbackParams) {
    return (
      <main className="page">
        <div className="kb-section">
          {deniedCode !== null && error === null ? (
            <p className="form__notice">
              Facebook did not grant access. Nothing was connected and nothing changed.
            </p>
          ) : (
            <p className="form__error" role="alert">
              {error ?? MISSING_PARAMS_MESSAGE}
            </p>
          )}
          <div className="page__actions">
            <button
              className="button button--primary"
              type="button"
              disabled={isBusy || !businessId}
              onClick={handleRetry}
            >
              {isBusy ? 'Opening Facebook' : 'Try again'}
            </button>
            <Link className="button" to={INTEGRATIONS_PATH}>
              Back to Connected accounts
            </Link>
          </div>
        </div>
      </main>
    );
  }

  if (choice !== null) {
    return (
      <main className="page">
        <div className="kb-section">
          <fieldset className="field" disabled={isBusy}>
            <legend className="field__label">Which Page should we post to?</legend>
            <div className="platform-picker" role="radiogroup" aria-label="Facebook Page">
              {choice.pages.map((page) => {
                const isSelected = page.id === pageId;
                return (
                  <label
                    className={
                      isSelected ? 'platform-chip platform-chip--selected' : 'platform-chip'
                    }
                    key={page.id}
                  >
                    <input
                      className="visually-hidden"
                      type="radio"
                      name="facebook-page"
                      value={page.id}
                      checked={isSelected}
                      onChange={() => setPageId(page.id)}
                    />
                    {page.name}
                  </label>
                );
              })}
            </div>
          </fieldset>
          <div className="page__actions">
            <button
              className="button button--primary"
              type="button"
              disabled={isBusy || pageId === null}
              onClick={handleSelect}
            >
              {isBusy ? 'Connecting' : 'Connect this Page'}
            </button>
          </div>
        </div>
      </main>
    );
  }

  if (!isBusinessLoading && !businessId) {
    return (
      <main className="page">
        <div className="kb-section">
          <p className="form__notice">
            {businessError ??
              'No business is linked to this account yet. Finish setup and connect Facebook from Connected accounts.'}
          </p>
          <div className="page__actions">
            <Link className="button button--primary" to="/onboarding">
              Finish setup
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return <FullPageStatus message="Connecting your Facebook Page" />;
}
